import React, { useCallback, useMemo, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import BookingRow, { BOOKING_STATUSES } from '@/components/BookingRow';
import TableStateBody from '@/components/TableStateBody';
import PageHeader from '@/components/PageHeader';
import TableFilters from '@/components/TableFilters';
import useFetchList from '@/hooks/useFetchList';

const BOOKING_COLUMNS = 12;

function matchesSearch(booking, query) {
  if (!query) return true;
  const s = query.toLowerCase();
  const haystack = `${booking.partner_name || ''} ${booking.service_name || ''} ${booking.id}`.toLowerCase();
  return haystack.includes(s);
}

export default function Bookings() {
  const {
    data: bookings,
    setData: setBookings,
    loading,
    refresh: fetchBookings,
  } = useFetchList('/admin/bookings', { errorLabel: 'bookings' });

  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  const handleStatusChange = useCallback(
    async (bookingId, newStatus) => {
      setUpdatingId(bookingId);
      try {
        const { data } = await api.patch(`/admin/bookings/${bookingId}/status`, {
          status: newStatus,
        });
        setBookings((prev) => prev.map((b) => (b.id === bookingId ? data : b)));
        toast.success(`Booking status updated to ${newStatus.replace(/_/g, ' ')}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to update booking status');
      } finally {
        setUpdatingId(null);
      }
    },
    [setBookings],
  );

  const filtered = useMemo(
    () =>
      bookings.filter((b) => {
        if (statusFilter !== 'all' && b.status !== statusFilter) return false;
        return matchesSearch(b, search);
      }),
    [bookings, statusFilter, search],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Manage Bookings"
        subtitle="Operational view of all bookings across BeautyMateUK"
        onRefresh={fetchBookings}
        loading={loading}
        refreshTestId="btn-refresh-bookings"
      />

      <Card className="p-4 border-slate-200">
        <TableFilters
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by partner, service or booking ID"
          searchTestId="bookings-search"
          statusValue={statusFilter}
          onStatusChange={setStatusFilter}
          statuses={BOOKING_STATUSES}
          statusTestId="bookings-status-filter"
        />

        <div className="rounded-lg border border-slate-200 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Booking</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Partner</TableHead>
                <TableHead>Service</TableHead>
                <TableHead>Service £</TableHead>
                <TableHead>Travel £</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Commission</TableHead>
                <TableHead>Payout</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableStateBody
                loading={loading}
                isEmpty={filtered.length === 0}
                colSpan={BOOKING_COLUMNS}
                emptyText="No bookings found"
              >
                {filtered.map((b) => (
                  <BookingRow
                    key={b.id}
                    booking={b}
                    updatingId={updatingId}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </TableStateBody>
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
